/* ============================================================
   TOTELLE — theme.js
   Vanilla, zéro dépendance. Custom elements partagés.
   ============================================================ */

/* ---------- header sticky / transparent ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.transparent = this.hasAttribute('data-transparent');
    this.onScroll = this.update.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  update() {
    const scrolled = window.scrollY > 24;
    this.classList.toggle('is-scrolled', scrolled);
  }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- drawer (menu mobile) ---------- */
class DrawerToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.targetId = this.getAttribute('data-target');
    this.button?.addEventListener('click', () => this.toggle());
  }
  toggle() {
    const drawer = document.getElementById(this.targetId);
    if (!drawer) return;
    const open = drawer.toggleAttribute('open');
    this.button.setAttribute('aria-expanded', open);
    document.body.style.overflow = open ? 'hidden' : '';
  }
}
customElements.define('drawer-toggle', DrawerToggle);

/* ---------- carousel scroll-snap + pastilles ---------- */
class TCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.carousel__track');
    this.dotsWrap = this.querySelector('.carousel__dots');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    if (this.dotsWrap && this.slides.length > 1) this.buildDots();
    this.track.addEventListener('scroll', () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.syncDots(), 80);
    }, { passive: true });
  }
  buildDots() {
    this.slides.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `slide ${i + 1}`);
      if (i === 0) b.setAttribute('aria-current', 'true');
      b.addEventListener('click', () => {
        this.slides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
      this.dotsWrap.appendChild(b);
    });
  }
  syncDots() {
    if (!this.dotsWrap) return;
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let active = 0;
    this.slides.forEach((s, i) => {
      if (s.offsetLeft <= mid) active = i;
    });
    this.dotsWrap.querySelectorAll('button').forEach((b, i) => {
      if (i === active) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  }
}
customElements.define('t-carousel', TCarousel);

/* ---------- lightbox (image ou vidéo) — robuste iOS ----------
   Correctifs (audit) :
   - close()/open() liberent le decodeur video iOS (pause + removeAttribute('src') + strip <source> + load())
     -> corrige "2e media = ecran blanc" (le decodeur du 1er restait alloue et saturait le budget iOS).
   - branche video : muted + autoplay (iOS ne joue pas une video non-muette injectee) + poster (frame
     instantanee) + spinner + handlers loadeddata/error. Pas d'unmute auto (WebKit re-pauserait).
   - bouton close = enfant DIRECT de .lightbox : un position:fixed dans un ancetre transforme (scale)
     s'ancre a cet ancetre, pas au viewport -> le safe-area etait inerte, d'ou "X inatteignable".
   - fermeture facile : fond ET letterbox ferment ; image = tap n'importe ou ferme ; sur une video on
     protege seulement la zone <video> (controles natifs).
   - preload HD "a l'intention" (pointerdown/pointerenter/focus + requestIdleCallback) : plus de course LCP.
   - token : invalide les callbacks async apres un close/reopen. */
const LightboxPreload = new Set();
function preloadLightboxImage(src) {
  if (!src || LightboxPreload.has(src)) return;
  LightboxPreload.add(src);
  const idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 120); };
  idle(function () {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  });
}

class LightboxOpener extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      e.preventDefault();
      const src = this.getAttribute('data-src');
      const type = this.getAttribute('data-type') || 'image';
      const innerImg = this.querySelector('img');
      const innerVid = this.querySelector('video');
      const placeholder = (innerImg && (innerImg.currentSrc || innerImg.src)) || '';
      const poster = (innerVid && innerVid.getAttribute('poster')) || '';
      if (src) Lightbox.open(src, type, placeholder, poster);
    });
    // preload HD "a l'intention" : images uniquement, au geste (jamais au scroll) -> zero concurrence LCP
    const type = this.getAttribute('data-type') || 'image';
    const src = this.getAttribute('data-src');
    if (type !== 'image' || !src) return;
    const warm = () => preloadLightboxImage(src);
    this.addEventListener('pointerdown', warm, { once: true, passive: true });
    this.addEventListener('pointerenter', warm, { once: true, passive: true });
    this.addEventListener('focus', warm, { once: true });
  }
}
customElements.define('lightbox-opener', LightboxOpener);

const Lightbox = {
  el: null,
  token: 0,
  openType: 'image',
  ensure() {
    if (this.el) return this.el;
    this.el = document.createElement('div');
    this.el.className = 'lightbox';
    // bouton close = enfant DIRECT de .lightbox (hors du .lightbox__content transforme -> ancrage viewport)
    this.el.innerHTML = `
      <button type="button" class="lightbox__close" aria-label="close">
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4l12 12M16 4L4 16"/></svg>
      </button>
      <button type="button" class="lightbox__sound" aria-label="son on/off" hidden>
        <svg class="lb-snd-off" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M17 9l4 6M21 9l-4 6"/></svg>
        <svg class="lb-snd-on" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16 8a5 5 0 0 1 0 8M18.5 5.5a9 9 0 0 1 0 13"/></svg>
      </button>
      <div class="lightbox__content">
        <div class="lightbox__media"></div>
      </div>`;
    this.soundBtn = this.el.querySelector('.lightbox__sound');
    this.el.addEventListener('click', (e) => {
      if (e.target.closest('.lightbox__close')) { this.close(); return; }
      if (e.target.closest('.lightbox__sound')) return;            // bouton son custom : geré a part
      if (this.openType === 'image') { this.close(); return; }     // image : tap n'importe ou ferme
      if (e.target.closest('video')) return;                       // video : tap sur la video = ne ferme pas
      this.close();                                                // fond / letterbox = ferme
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    document.body.appendChild(this.el);
    return this.el;
  },
  // libere le decodeur d'une <video> pour que iOS rende le "slot" media
  teardownVideo(wrap) {
    const v = wrap && wrap.querySelector('video');
    if (!v) return;
    try { v.pause(); } catch (_) {}
    v.removeAttribute('src');
    while (v.firstChild) v.removeChild(v.firstChild);   // enleve aussi d'eventuels <source>
    try { v.load(); } catch (_) {}
  },
  open(src, type, placeholder, poster) {
    const el = this.ensure();
    const wrap = el.querySelector('.lightbox__media');
    const t = ++this.token;
    this.openType = type;
    this.teardownVideo(wrap);          // libere une eventuelle video precedente AVANT d'injecter
    wrap.classList.remove('is-loading');

    if (type === 'video') {
      wrap.classList.add('is-loading');
      const posterAttr = poster ? (' poster="' + poster + '"') : '';
      // PAS de `controls` : les controles natifs iOS (mute EN HAUT A DROITE, plein-ecran/PiP en haut a
      // gauche) chevauchent notre bouton close. Video "story" : autoplay muet + loop + poster ; le son
      // se gere avec notre bouton custom (bas-centre, hors zone des controles natifs).
      wrap.innerHTML = '<video src="' + src + '" autoplay muted loop playsinline preload="auto"' + posterAttr + '></video>';
      const v = wrap.querySelector('video');
      v.muted = true;                  // attribut muted via innerHTML peu fiable sur WebKit -> on force
      const clear = () => { if (this.token === t) wrap.classList.remove('is-loading'); };
      v.addEventListener('loadeddata', clear, { once: true });
      v.addEventListener('canplay', clear, { once: true });
      v.addEventListener('error', clear, { once: true });
      // bouton son custom (enfant DIRECT de .lightbox -> pas piege par le transform du content)
      if (this.soundBtn) {
        this.soundBtn.hidden = false;
        this.soundBtn.classList.remove('is-on');
        const toggle = (ev) => {
          if (ev) ev.stopPropagation();
          v.muted = !v.muted;
          if (!v.muted) { const pp = v.play(); if (pp && pp.catch) pp.catch(() => {}); }
          this.soundBtn.classList.toggle('is-on', !v.muted);
        };
        this.soundBtn.onclick = toggle;
        v.addEventListener('click', toggle);   // tap sur la video = son on/off aussi
      }
      const p = v.play && v.play();    // demarre dans le geste
      if (p && p.catch) p.catch(() => {});
    } else if (placeholder && placeholder !== src) {
      if (this.soundBtn) this.soundBtn.hidden = true;
      wrap.innerHTML = '<img src="' + placeholder + '" alt="" decoding="async">';
      wrap.classList.add('is-loading');
      const img = wrap.querySelector('img');
      const hd = new Image();
      hd.decoding = 'async';
      hd.onload = () => {
        if (this.token === t && img.isConnected) { img.src = src; wrap.classList.remove('is-loading'); }
        LightboxPreload.add(src);
      };
      hd.onerror = () => { if (this.token === t) wrap.classList.remove('is-loading'); };
      hd.src = src;
    } else {
      if (this.soundBtn) this.soundBtn.hidden = true;
      wrap.classList.add('is-loading');
      wrap.innerHTML = '<img src="' + src + '" alt="" decoding="async">';
      const img = wrap.querySelector('img');
      const done = () => { if (this.token === t) wrap.classList.remove('is-loading'); };
      if (img.complete && img.naturalWidth) done();
      else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
    }

    el.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  },
  close() {
    if (!this.el) return;
    this.token++;                      // invalide les swaps async en vol
    const wrap = this.el.querySelector('.lightbox__media');
    this.teardownVideo(wrap);          // libere le decodeur AVANT de detacher
    if (this.soundBtn) { this.soundBtn.hidden = true; this.soundBtn.onclick = null; }
    this.el.removeAttribute('open');
    wrap.innerHTML = '';
    wrap.classList.remove('is-loading');
    document.body.style.overflow = '';
  }
};
window.TotelleLightbox = Lightbox;

/* slide video : tap n'importe ou sur la video ouvre le plein ecran (persona 55-65,
   plus de mini bouton loupe a viser). Le bouton son garde son role local :
   on le laisse passer et on ne traite QUE les clics hors du bouton son. */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-sound-toggle]')) return;      // sound button = pas de plein ecran
  const zoom = e.target.closest('[data-video-zoom]');
  if (!zoom) return;
  e.preventDefault();
  e.stopPropagation();
  const gv = zoom.querySelector('video');
  const gp = (gv && gv.getAttribute('poster')) || '';
  if (zoom.dataset.src) Lightbox.open(zoom.dataset.src, 'video', '', gp);
}, true);
/* clavier : Enter / Space sur la slide video (role=button, tabindex=0) declenche le plein ecran */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const zoom = e.target.closest('[data-video-zoom]');
  if (!zoom) return;
  e.preventDefault();
  const gv = zoom.querySelector('video');
  const gp = (gv && gv.getAttribute('poster')) || '';
  if (zoom.dataset.src) Lightbox.open(zoom.dataset.src, 'video', '', gp);
});

/* variant-picker + bundle-selector retirés : sélecteurs coloris/hardware/bundles
   supprimés de la buy-box (gérés par une app de bundle). */

/* ---------- formulaire produit (ATC ajax → /cart) ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (e) => this.submit(e));
  }
  async submit(e) {
    e.preventDefault();
    const btn = this.form.querySelector('[type="submit"]');
    const msg = this.querySelector('[data-form-error]');
    if (msg) { msg.hidden = true; msg.textContent = ''; }
    btn.setAttribute('disabled', '');
    btn.classList.add('is-loading');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(this.form)
      });
      if (!res.ok) {
        // récupère le message exact renvoyé par Shopify (ex. 422 « sold out »)
        let detail = '';
        try { const j = await res.json(); detail = j.description || j.message || ''; } catch (_) {}
        throw new Error(detail);
      }
      // ouvre le panier tiroir (glisse de la droite, aucune navigation de page)
      document.dispatchEvent(new CustomEvent('cart:added'));
    } catch (err) {
      if (msg) {
        msg.textContent = (err && err.message) ? err.message : 'sorry — this item can’t be added right now.';
        msg.hidden = false;
      }
    } finally {
      btn.removeAttribute('disabled');
      btn.classList.remove('is-loading');
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- sticky ATC mobile ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const targetSel = this.getAttribute('data-observe') || '[data-main-atc]';
    const target = document.querySelector(targetSel);
    if (!target || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      this.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(target);
    this.querySelector('button')?.addEventListener('click', () => {
      // soumet le formulaire produit natif (poste vers /cart/add, comportement standard Shopify)
      document.getElementById('product-form-main')?.requestSubmit();
    });
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------- announcement bar rotation ---------- */
class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-message]'));
    if (this.items.length < 2) return;
    // durée d'affichage réglable via le curseur (data-interval, ms) ; défaut 4000ms si absent/incohérent.
    // le fondu (500ms, CSS) est INCLUS dans ce cycle : la bascule tombe pile toutes les `interval` ms.
    let interval = parseInt(this.getAttribute('data-interval'), 10);
    if (isNaN(interval) || interval < 1000) interval = 4000;
    // cross-fade : on bascule .is-active (opacité + transition gérées en CSS), plus de hidden.
    this.items.forEach((el, i) => {
      el.hidden = false;
      el.classList.toggle('is-active', i === 0);
      el.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    });
    this.index = 0;
    setInterval(() => {
      const sortant = this.index;
      this.index = (this.index + 1) % this.items.length;
      this.items[sortant].classList.remove('is-active');
      this.items[sortant].setAttribute('aria-hidden', 'true');
      this.items[this.index].classList.add('is-active');
      this.items[this.index].setAttribute('aria-hidden', 'false');
    }, interval);
  }
}
customElements.define('announcement-rotator', AnnouncementRotator);

/* ---------- quantité cart ---------- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-qty-change]');
  if (!btn) return;
  const wrap = btn.closest('.qty');
  const input = wrap?.querySelector('input');
  if (!input) return;
  const delta = parseInt(btn.dataset.qtyChange, 10);
  const next = Math.max(parseInt(input.min || '0', 10), (parseInt(input.value, 10) || 0) + delta);
  input.value = next;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------- UGC carousel : marquee auto-scroll + swipe + son + IntersectionObserver ---------- */
class UGCCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ugc-track]');
    if (!this.track) return;
    this.speed = Math.max(8, parseFloat(this.dataset.speed || '40')); // secondes / cycle
    this.offset = 0;
    this.dragging = false;
    this.pausedByUser = false;
    this.lastT = null;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.measure();
    this._onResize = () => this.measure();
    window.addEventListener('resize', this._onResize, { passive: true });

    this.setupVideoObserver();
    this.setupDrag();

    // fleches (persona 55+) : un pas = une carte ; pause du marquee puis reprise apres 4s
    this.querySelectorAll('[data-ugc-arrow]').forEach((b) => {
      b.addEventListener('click', () => this.stepBy(b.dataset.ugcArrow === 'next' ? 1 : -1));
    });

    // perf : le marquee (rAF permanent) ne tourne QUE quand la section est visible.
    // Hors écran il consommait du CPU sur toute la page (galerie comprise).
    this.visible = false;
    this.sectionIO = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        this.visible = e.isIntersecting;
        if (this.visible && !this.reduce && !this.raf) {
          this.lastT = null;
          this.raf = requestAnimationFrame(this.tick.bind(this));
        } else if (!this.visible && this.raf) {
          cancelAnimationFrame(this.raf);
          this.raf = null;
        }
      });
    }, { rootMargin: '100px 0px' });
    this.sectionIO.observe(this);
  }
  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this._onResize);
    this.io?.disconnect();
    this.sectionIO?.disconnect();
  }
  measure() {
    // la liste est dupliquée : la largeur d'un cycle = moitié du scrollWidth
    this.half = this.track.scrollWidth / 2;
  }
  tick(t) {
    if (this.lastT == null) this.lastT = t;
    const dt = Math.min(0.05, (t - this.lastT) / 1000);
    this.lastT = t;
    if (!this.dragging && !this.pausedByUser && this.half > 0) {
      this.offset -= (this.half / this.speed) * dt;
      if (-this.offset >= this.half) this.offset += this.half;
      this.apply();
    }
    this.raf = this.visible ? requestAnimationFrame(this.tick.bind(this)) : null;
  }
  apply() { this.track.style.transform = `translate3d(${this.offset}px,0,0)`; }
  stepBy(dir) {
    const item = this.track.querySelector('.ugc__item');
    if (!item) return;
    const cs = getComputedStyle(this.track);
    const gap = parseFloat(cs.columnGap || cs.gap) || 12;
    const step = (item.getBoundingClientRect().width + gap) * dir;
    this.pausedByUser = true;
    clearTimeout(this.resumeT);
    if (!this.reduce) this.track.style.transition = 'transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1)';
    this.offset -= step;
    this.apply();
    clearTimeout(this._stepT);
    // le wrap modulo saute d'un demi-rail : on l'applique APRES la transition, sans animation
    this._stepT = setTimeout(() => {
      this.track.style.transition = '';
      this.wrap();
      this.apply();
      this.resumeT = setTimeout(() => { this.pausedByUser = false; this.lastT = null; }, 4000);
    }, 250);
  }
  wrap() {
    if (this.half <= 0) return;
    while (-this.offset >= this.half) this.offset += this.half;
    while (this.offset > 0) this.offset -= this.half;
  }
  setupDrag() {
    let startX = 0, startOffset = 0, moved = false;
    const down = (e) => {
      this.dragging = true; moved = false;
      startX = (e.touches ? e.touches[0].clientX : e.clientX);
      startOffset = this.offset;
      clearTimeout(this.resumeT);
      this.pausedByUser = false;
    };
    const move = (e) => {
      if (!this.dragging) return;
      const x = (e.touches ? e.touches[0].clientX : e.clientX);
      const dx = x - startX;
      if (Math.abs(dx) > 4) moved = true;
      this.offset = startOffset + dx;
      this.wrap();
      this.apply();
    };
    const up = () => {
      if (!this.dragging) return;
      this.dragging = false;
      if (moved) {
        // pause l'auto pendant 2s d'inactivité puis reprise
        this.pausedByUser = true;
        clearTimeout(this.resumeT);
        this.resumeT = setTimeout(() => { this.pausedByUser = false; this.lastT = null; }, 2000);
      }
    };
    this.track.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move, { passive: true });
    window.addEventListener('mouseup', up);
    this.track.addEventListener('touchstart', down, { passive: true });
    this.track.addEventListener('touchmove', move, { passive: true });
    this.track.addEventListener('touchend', up);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }
  setupVideoObserver() {
    const vids = this.querySelectorAll('video');
    if (!('IntersectionObserver' in window)) { vids.forEach((v) => v.play?.().catch(() => {})); return; }
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target;
        if (en.isIntersecting) { v.play?.().catch(() => {}); }
        else { v.pause?.(); }
      });
    }, { root: null, threshold: 0.25 });
    vids.forEach((v) => this.io.observe(v));
  }
}
customElements.define('ugc-carousel', UGCCarousel);

/* ---------- contrôleur son GLOBAL : une seule vidéo sonore sur toute la page ---------- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-sound-toggle]');
  if (!btn) return;
  e.preventDefault();
  const scope = btn.closest('[data-video-item]');
  const video = scope?.querySelector('video');
  if (!video) return;
  const willUnmute = video.muted;
  // couper le son de TOUTES les vidéos contrôlées de la page (UGC + fabrication)
  document.querySelectorAll('[data-video-item] video').forEach((v) => {
    v.muted = true;
    const b = v.closest('[data-video-item]')?.querySelector('[data-sound-toggle]');
    b?.setAttribute('aria-pressed', 'false');
    b?.classList.remove('is-on');
  });
  if (willUnmute) {
    video.muted = false;
    video.play().catch(() => {});
    btn.setAttribute('aria-pressed', 'true');
    btn.classList.add('is-on');
  }
});

/* ---------- io-video : joue la vidéo uniquement quand visible (section fabrication) ---------- */
class IOVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    if (!('IntersectionObserver' in window)) { this.video.play?.().catch(() => {}); return; }
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) this.video.play?.().catch(() => {});
        else this.video.pause?.();
      });
    }, { threshold: 0.25 });
    this.io.observe(this.video);
  }
  disconnectedCallback() { this.io?.disconnect(); }
}
customElements.define('io-video', IOVideo);

/* ---------- galerie PDP : un seul carousel, mobile + desktop ----------
   Translation CSS et non scroll-snap : sur un scroller natif, la boucle infinie
   impose de repositionner scrollLeft, ce qui se voit toujours (saut + inertie).
   Clones en tete/queue + translate3d => boucle propre dans les deux sens.
   `touch-action: pan-y` (CSS) laisse le scroll vertical au navigateur. */
class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.pdpg__track');
    this.viewport = this.querySelector('.pdpg__viewport');
    if (!this.track || !this.viewport) return;

    this.loop = this.dataset.loop === 'true';
    this.count = this.track.children.length;
    this.thumbs = Array.from(this.querySelectorAll('.pdpg__thumb'));
    this.prevBtn = this.querySelector('.pdpg__arrow--prev');
    this.nextBtn = this.querySelector('.pdpg__arrow--next');

    this.cloned = this.loop && this.count > 1;
    if (this.cloned) this.addClones();
    this.index = 0;
    this.pos = this.cloned ? 1 : 0;

    this.bind();
    this.querySelectorAll('video').forEach((v) => this.armerReprise(v));
    this.watchVisibility();
    this.place(false);
    this.sync();
    this.preloadAssets();
  }

  /* Precharge TOUT le media de la galerie des que la page est au repos (apres le LCP) :
     - chaque image de slide en lazy passe en eager -> swipe instantane, plus de "2e image blanche"
     - chaque poster de video est prechauffe -> ouverture lightbox video = frame immediate
     - le HD lightbox (data-src) de chaque slide image est prechauffe -> zoom instantane
     Cout accepte : sur une PDP, les images DU PRODUIT sont le contenu principal ; on ne prend
     que le repos du navigateur (requestIdleCallback) pour ne jamais concurrencer le LCP. */
  preloadAssets() {
    // liste des VRAIES slides (hors clones), dans l'ordre logique
    this._realSlides = Array.from(this.querySelectorAll('.pdpg__slide:not([data-clone])'));
    // fenetre initiale autour de la slide 0 (courante + voisines) -> swipe immediat sans saturer la 4G
    this.preloadWindow();
  }

  /* Precharge une FENETRE autour de la slide courante (index-1 .. index+2) : la prochaine image que
     la cliente va voir est deja chargee AVANT qu'elle swipe, sans telecharger les 16 medias d'un coup
     (ce qui, sur 4G, mettait justement la 2e image en file d'attente derriere les 14 autres). */
  preloadWindow() {
    const list = this._realSlides || (this._realSlides = Array.from(this.querySelectorAll('.pdpg__slide:not([data-clone])')));
    const n = list.length;
    if (!n) return;
    const cur = ((this.index % n) + n) % n;
    for (let d = -1; d <= 2; d++) {
      const slide = list[(((cur + d) % n) + n) % n];
      if (!slide) continue;
      const img = slide.querySelector('img');
      if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      const vid = slide.querySelector('video');
      const poster = vid && vid.getAttribute('poster');
      if (poster && !this._warm(poster)) { const pi = new Image(); pi.decoding = 'async'; pi.src = poster; }
      // HD lightbox de la slide courante seulement (zoom instantane sans surcharger)
      if (d === 0) {
        const hd = slide.getAttribute('data-src');
        if (hd && slide.getAttribute('data-type') === 'image' && !this._warm(hd)) {
          const hi = new Image(); hi.decoding = 'async'; hi.src = hd;
        }
      }
    }
  }
  _warm(u) { (this._warmed || (this._warmed = new Set())); if (this._warmed.has(u)) return true; this._warmed.add(u); return false; }

  /* la video ne joue que si la galerie est reellement a l'ecran */
  watchVisibility() {
    this.onScreen = true;
    if (!('IntersectionObserver' in window)) return;
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { this.onScreen = en.isIntersecting; });
      this.syncVideo();
    }, { threshold: 0.25 });
    this.io.observe(this);
  }

  disconnectedCallback() { this.io?.disconnect(); this.ro?.disconnect(); cancelAnimationFrame(this._anim); }

  addClones() {
    const real = Array.from(this.track.children);
    this.track.insertBefore(this.makeClone(real[this.count - 1]), real[0]);
    this.track.appendChild(this.makeClone(real[0]));
  }

  /* un clone ne doit jamais etre un 2e lecteur video ni voler la priorite de chargement */
  makeClone(slide) {
    const c = slide.cloneNode(true);
    c.setAttribute('aria-hidden', 'true');
    c.dataset.clone = 'true';
    c.removeAttribute('id');
    const v = c.querySelector('video');
    if (v) {
      const img = document.createElement('img');
      const poster = v.getAttribute('poster');
      if (poster) img.src = poster;
      img.alt = '';
      img.loading = 'lazy';
      v.replaceWith(img);
    }
    c.querySelectorAll('img').forEach((i) => { i.loading = 'lazy'; });
    return c;
  }

  bind() {
    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
    });
    this.track.addEventListener('transitionend', (e) => {
      if (e.target !== this.track || e.propertyName !== 'transform') return;
      this.finTransition();
    });
    this.bindDrag();
    window.addEventListener('resize', () => this.place(false), { passive: true });
    // toute variation de largeur du viewport (WebView, barre d'adresse, rotation,
    // zoom texte) recale la position instantanement — c'est LE garde-fou anti
    // « slide mordue » / « page decalee », en px deterministes.
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.place(false));
      this.ro.observe(this.viewport);
    }
  }

  /* Le geste est ecoute sur le VIEWPORT et non sur le track : les fleches sont
     posees au-dessus de l'image, et sur le track elles auraient cree deux zones
     mortes au swipe sur les bords. Ici le swipe reste actif sur toute la largeur. */
  bindDrag() {
    let startX = 0; let startY = 0; let dx = 0;
    let dragging = false; let decided = false; let horizontal = false;
    const w = () => this.viewport.clientWidth;

    this.viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true; decided = false; horizontal = false; dx = 0;
      startX = e.clientX; startY = e.clientY;
      this.moved = false;
      cancelAnimationFrame(this._anim);   // stoppe l'animation en cours, le doigt prend la main
      this._anim = null;
      this.track.classList.remove('is-animating');
    });

    this.viewport.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const ddx = e.clientX - startX;
      const ddy = e.clientY - startY;
      if (!decided) {
        if (Math.abs(ddx) < 6 && Math.abs(ddy) < 6) return;
        decided = true;
        horizontal = Math.abs(ddx) > Math.abs(ddy);
        if (horizontal) this.viewport.setPointerCapture?.(e.pointerId);
      }
      if (!horizontal) return;
      dx = ddx;
      if (Math.abs(dx) > 4) this.moved = true;
      // % du track (et non px du viewport) : si la WebView change la largeur en cours
      // de route, la position reste juste — le px n'est utilise que pour le doigt.
      this.track.style.transform = `translate3d(${-this.pos * w() + dx}px, 0, 0)`;
    });

    const end = () => {
      if (!dragging) return;
      dragging = false;
      const seuil = Math.max(40, w() * 0.15);
      if (horizontal && Math.abs(dx) > seuil) this.step(dx < 0 ? 1 : -1);
      else this.place(true);
      dx = 0;
    };
    this.viewport.addEventListener('pointerup', end);
    this.viewport.addEventListener('pointercancel', end);

    /* un swipe ne doit ni ouvrir la lightbox, ni declencher une fleche ou le bouton son */
    this.viewport.addEventListener('click', (e) => {
      if (this.moved) { e.preventDefault(); e.stopPropagation(); this.moved = false; }
    }, true);
  }

  /* Fin d'animation : la slide courante est enfin reellement visible, c'est le
     seul moment ou lancer la video sans que Chrome la re-pause aussitot.
     Double signal (transitionend + minuterie) : si la transition est interrompue,
     `transitionend` ne part jamais et la video resterait muette-figee. */
  finTransition() {
    clearTimeout(this._tFin);
    this._enTransition = false;
    this.settle();
    this.syncVideo();
  }

  /* Animation pilotee en JS (rAF, px directs) et NON par transition CSS :
     constate en prod, la transition sur ce track ne demarrait pas sur certains
     moteurs (matrice figee a l'identite, transitionend jamais emis -> galerie
     coincee a mi-course). Les ecritures directes, elles, rendent partout —
     c'est le mecanisme du marquee UGC. Position en px recalculee a l'ecriture ;
     la justesse face aux changements de largeur (WebViews FB/IG, barre
     d'adresse, rotation) est assuree par le ResizeObserver du viewport. */
  currentTx() {
    const m = getComputedStyle(this.track).transform;
    if (m && m !== 'none') {
      const p = m.match(/-?[\d.e+-]+/g);
      if (p && p.length >= 6) return parseFloat(p[4]) || 0;
    }
    return 0;
  }

  place(animate) {
    const target = -this.pos * this.viewport.clientWidth;
    cancelAnimationFrame(this._anim);
    this._anim = null;
    this.track.classList.remove('is-animating');   // aucune transition CSS, jamais
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduce) {
      this.track.style.transform = `translate3d(${target}px, 0, 0)`;
      if (animate) this.finTransition();
      return;
    }
    this._enTransition = true;
    clearTimeout(this._tFin);
    // filet : si le rAF est gele (onglet cache, low power), on SNAPPE a la cible —
    // la galerie ne reste jamais a mi-course.
    this._tFin = setTimeout(() => {
      if (this._anim) {
        cancelAnimationFrame(this._anim);
        this._anim = null;
        this.track.style.transform = `translate3d(${target}px, 0, 0)`;
      }
      this.finTransition();
    }, 600);
    const from = this.currentTx();
    const dur = 380;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const stepFn = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      this.track.style.transform = `translate3d(${from + (target - from) * ease(p)}px, 0, 0)`;
      if (p < 1) this._anim = requestAnimationFrame(stepFn);
      else { this._anim = null; this.finTransition(); }
    };
    this._anim = requestAnimationFrame(stepFn);
  }

  step(d) {
    if (this.count < 2) return;
    if (this.cloned) {
      this.pos += d;
      this.index = (((this.pos - 1) % this.count) + this.count) % this.count;
    } else {
      const n = this.index + d;
      if (n < 0 || n > this.count - 1) { this.place(true); return; }
      this.index = n;
      this.pos = n;
    }
    this.place(true);
    this.sync();
  }

  goTo(i) {
    if (i === this.index) return;
    this.index = i;
    this.pos = this.cloned ? i + 1 : i;
    this.place(true);
    this.sync();
  }

  /* arrive sur un clone : on retombe sur la vraie slide, sans transition */
  settle() {
    if (!this.cloned) return;
    if (this.pos === 0) { this.pos = this.count; this.place(false); }
    else if (this.pos === this.count + 1) { this.pos = 1; this.place(false); }
  }

  sync() {
    this.thumbs.forEach((t, i) => {
      if (i === this.index) {
        t.setAttribute('aria-current', 'true');
        this.revealThumb(t);
      } else {
        t.removeAttribute('aria-current');
      }
    });
    if (!this.loop) {
      this.prevBtn?.toggleAttribute('disabled', this.index === 0);
      this.nextBtn?.toggleAttribute('disabled', this.index === this.count - 1);
    }
    this.preloadWindow();   // precharge la prochaine slide a chaque navigation
    this.syncVideo();
  }

  /* Garde la vignette active visible DANS SA BANDE. Surtout pas `scrollIntoView` :
     il fait aussi defiler la page, ce qui (a) deplace la page sous le doigt au clic
     sur une vignette et (b) fait sortir la galerie du seuil de l'IntersectionObserver,
     qui met alors la video en pause juste apres l'avoir lancee. */
  revealThumb(t) {
    const wrap = t.parentElement;
    if (!wrap || wrap.scrollWidth <= wrap.clientWidth) return;
    /* centre la vignette dans la fenetre, puis borne : aux extremites la bande
       se cale sur le bord au lieu de laisser du vide. */
    const cible = t.offsetLeft - (wrap.clientWidth - t.offsetWidth) / 2;
    const max = wrap.scrollWidth - wrap.clientWidth;
    const left = Math.max(0, Math.min(cible, max));
    /* On POSE la position (affectation directe de `scrollLeft`) au lieu de la
       demander au navigateur via `scrollTo({behavior:'smooth'})` : mesure faite,
       le scroll lisse se fait re-snapper a 0 sur cette bande. La fluidite est
       laissee a `scroll-behavior: smooth` (CSS) — l'arrivee est la meme, animee
       ou non. `scroll-snap` est coupe pendant l'operation puis rendu : il reste
       actif pour le defilement au doigt et ne deplace pas la position posee. */
    clearTimeout(this._tSnap);
    wrap.style.scrollSnapType = 'none';
    void wrap.offsetWidth;               // applique le style AVANT de scroller
    wrap.scrollLeft = left;
    this._tSnap = setTimeout(() => { wrap.style.scrollSnapType = ''; }, 600);
  }

  /* une seule video peut jouer : celle de la slide courante, et seulement si la
     galerie est a l'ecran. Toutes les autres sont mises en pause et remuettes. */
  syncVideo() {
    const courante = this.track.children[this.pos];
    const active = courante ? courante.querySelector('video') : null;
    this.querySelectorAll('video').forEach((v) => {
      if (v === active) return;
      this.setPlaying(v, false);
      v.muted = true;
      const b = v.closest('[data-video-item]')?.querySelector('[data-sound-toggle]');
      b?.setAttribute('aria-pressed', 'false');
      b?.classList.remove('is-on');
    });
    if (!active) return;
    if (!this.onScreen) { this.setPlaying(active, false); return; }
    /* on ne lance PAS pendant l'animation : la slide est encore masquee par
       l'`overflow: hidden`, Chrome pauserait la video juste apres. On note
       l'intention, `finTransition()` rappellera syncVideo au bon moment. */
    if (this._enTransition) { active._veutJouer = true; return; }
    this.setPlaying(active, true);
  }

  /* Deux pieges cumules, tous deux constates en test :
     1. `play()` est ASYNCHRONE — un `pause()` emis pendant qu'il est en vol
        l'annule silencieusement. On attend donc sa resolution avant de pauser.
     2. Chrome met de lui-meme en pause une video muette qu'il juge invisible.
        Pendant les 420ms de transition, la slide est encore masquee par
        l'`overflow: hidden` du viewport : le navigateur pause DERRIERE NOTRE DOS,
        sans passer par `.pause()`. Un garde « etat deja voulu » bloquait alors
        toute reprise (2 cycles sur 5 restaient en pause).
        -> quand on veut jouer, on RE-lance des que la video est en pause. */
  setPlaying(v, veutJouer) {
    if (veutJouer) {
      v._veutJouer = true;
      if (!v.paused) return;
      const p = v.play?.();
      v._enVol = p ? p.catch(() => {}) : null;
    } else {
      if (v._veutJouer === false) return;
      v._veutJouer = false;
      Promise.resolve(v._enVol).then(() => {
        if (v._veutJouer === false) v.pause();
      });
      v._enVol = null;
    }
  }

  /* Filet : si le navigateur pause la video alors qu'on la veut en lecture
     (cf. point 2 ci-dessus), on retente une fois, une frame plus tard. */
  armerReprise(v) {
    v.addEventListener('pause', () => {
      if (!v._veutJouer || v._repriseArmee) return;
      v._repriseArmee = true;
      setTimeout(() => {
        v._repriseArmee = false;
        if (v._veutJouer && v.paused && this.onScreen) this.setPlaying(v, true);
      }, 250);
    });
  }
}
customElements.define('pdp-gallery', PdpGallery);

/* ---------- worn, not carried : carrousel auto (mobile uniquement) ----------
   Desktop = grille CSS 5 colonnes, ce composant ne fait rien (matchMedia).
   Mobile = scroll-snap horizontal + avance automatique + pastilles, pause a
   l'interaction puis reprise. */
class WncCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.wnc__track');
    this.dotsWrap = this.querySelector('.wnc__dots');
    if (!this.track) return;
    this.items = Array.from(this.track.children);
    this.mq = window.matchMedia('(max-width: 989px)');
    if (this.items.length > 1) this.buildDots();
    this.track.addEventListener('scroll', () => {
      clearTimeout(this._st);
      this._st = setTimeout(() => this.syncDots(), 90);
    }, { passive: true });
    /* pause pendant que la cliente touche, reprise ~2s apres */
    ['pointerdown', 'touchstart'].forEach((e) =>
      this.track.addEventListener(e, () => this.pause(), { passive: true }));
    ['pointerup', 'touchend', 'mouseleave'].forEach((e) =>
      this.track.addEventListener(e, () => this.resumeSoon(), { passive: true }));
    this.mq.addEventListener('change', () => this.apply());
    this.apply();
  }
  disconnectedCallback() { this.stop(); }

  apply() {
    if (this.mq.matches) this.start();
    else { this.stop(); this.track.scrollLeft = 0; }
  }
  buildDots() {
    if (!this.dotsWrap) return;
    this.items.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `image ${i + 1}`);
      if (i === 0) b.setAttribute('aria-current', 'true');
      b.addEventListener('click', () => { this.pause(); this.goTo(i); this.resumeSoon(); });
      this.dotsWrap.appendChild(b);
    });
  }
  /* scroll pour centrer l'item i dans la fenetre, borne aux extremites */
  centerOf(i) {
    const it = this.items[i];
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = it.offsetLeft - (this.track.clientWidth - it.offsetWidth) / 2;
    return Math.max(0, Math.min(left, max));
  }
  goTo(i, animate) {
    this._i = i;
    const left = this.centerOf(i);
    if (animate === false) this.track.scrollLeft = left;
    else this.track.scrollTo({ left, behavior: 'smooth' });
    this.markDot(i);
  }
  markDot(i) {
    this.dotsWrap && this.dotsWrap.querySelectorAll('button').forEach((b, k) => {
      if (k === i) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  }
  syncDots() {
    if (!this.dotsWrap) return;
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let active = 0, best = Infinity;
    this.items.forEach((s, i) => {
      const c = s.offsetLeft + s.offsetWidth / 2;
      if (Math.abs(c - mid) < best) { best = Math.abs(c - mid); active = i; }
    });
    this.markDot(active);
    this._i = active;
  }
  start() {
    this.stop();
    this._timer = setInterval(() => this.next(), 4200);
  }
  stop() { clearInterval(this._timer); this._timer = null; }
  pause() { this.stop(); clearTimeout(this._resume); }
  resumeSoon() { clearTimeout(this._resume); this._resume = setTimeout(() => { if (this.mq.matches) this.start(); }, 2000); }
  next() {
    const i = ((this._i || 0) + 1) % this.items.length;
    this.goTo(i);
  }
}
customElements.define('wnc-carousel', WncCarousel);


